import { Box, Button, Modal, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';

// Mirror the backend cap on TaskRejectionRequest.Comments
// (validate:"required,max=1000") so the payload is always valid before submit.
const MAX_COMMENT_LENGTH = 1000;

const RejectModalContainer = styled(Box, {
  name: 'reject-modal-container',
  slot: 'Root',
})(() => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '500px',
  backgroundColor: 'var(--template-palette-background-paper)',
  border: '2px solid var(--template-palette-divider)',
  boxShadow:
    '0px 12px 24px 0px rgba(0, 0, 0, 0.2), 0px 16px 48px 0px rgba(0, 0, 0, 0.15)',
}));

type TRejectModalProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: (comment: string) => void;
  isLoading: boolean;
  busyLabel?: string;
};

const RejectModal = ({
  open,
  onClose,
  onConfirm,
  isLoading,
  busyLabel,
}: TRejectModalProps) => {
  const [comment, setComment] = useState<string>('');

  useEffect(() => {
    if (!open) {
      setComment('');
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={isLoading ? undefined : onClose}
      aria-labelledby="reject-modal-title"
      aria-describedby="reject-modal-description"
    >
      <RejectModalContainer sx={{ p: '16px' }}>
        <Box
          sx={{
            marginBottom: 1,
            pb: 1,
            borderBottom: '2px solid var(--template-palette-divider)',
          }}
        >
          <Typography variant="h5">Rejection Reason</Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            paddingTop: 1,
          }}
        >
          <TextField
            required
            size="small"
            label="Comment"
            variant="outlined"
            multiline
            minRows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            helperText={`${comment.length}/${MAX_COMMENT_LENGTH}`}
            slotProps={{
              htmlInput: { maxLength: MAX_COMMENT_LENGTH },
              formHelperText: { sx: { textAlign: 'right', m: 0 } },
            }}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            gap: 1,
            mt: 2,
          }}
        >
          <Button variant="outlined" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={comment.trim().length === 0 || isLoading}
            onClick={() => onConfirm(comment)}
          >
            {isLoading ? (busyLabel ?? 'Rejecting...') : 'Reject'}
          </Button>
        </Box>
      </RejectModalContainer>
    </Modal>
  );
};

export default RejectModal;
